import { useState, useCallback } from 'react';
import type { MediaItem } from '../types/movie';
import { getPosterUrl, formatYear, formatRuntime, calculateOurRating, getComingOnLabel } from '../utils/helpers';
import { GENRES, TV_GENRES } from '../utils/constants';
import { QuickActionButton } from './QuickActionButton';
import { RatingModal } from './RatingModal';
import { useAuth } from '../contexts/AuthContext';
import { useUserRating } from '../hooks/useRatings';
import { useMoviePrefetch, useTVPrefetch } from '../hooks/usePrefetch';

interface MovieCardProps {
  movie: MediaItem;
  onClick?: (movie: MediaItem) => void;
  showQuickActions?: boolean;
  userRating?: number | null; // Pre-fetched user rating from parent
  /** Image loading priority: 'high' for above-the-fold, 'low' for lazy loading */
  imagePriority?: 'high' | 'low';
}

export function MovieCard({ movie, onClick, showQuickActions = true, userRating: propUserRating, imagePriority = 'low' }: MovieCardProps) {
  const { user } = useAuth();
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [posterLoaded, setPosterLoaded] = useState(false);

  const mediaType = movie.media_type || 'movie';
  const year = formatYear(movie.release_date);
  const ourRating = calculateOurRating(movie.imdb_rating, movie.rt_rating);
  const comingOnLabel = getComingOnLabel(movie.release_date);

  // React Query for user rating (single source of truth)
  const { rating: cachedRating } = useUserRating(movie.id, mediaType);

  // Get user rating: prop > React Query cache
  const displayUserRating = propUserRating ?? cachedRating;

  const hasRatings = movie.imdb_rating || movie.rt_rating || ourRating || displayUserRating;
  const isTV = movie.media_type === 'tv';
  const genreMap = isTV ? TV_GENRES : GENRES;

  // Prefetch hooks (Phase 5 - hover prefetch)
  const { prefetch: prefetchMovie, cancel: cancelMoviePrefetch } = useMoviePrefetch();
  const { prefetch: prefetchTV, cancel: cancelTVPrefetch } = useTVPrefetch();

  // Handle hover to prefetch detail page
  const handleMouseEnter = useCallback(() => {
    if (isTV) {
      prefetchTV(movie.id);
    } else {
      prefetchMovie(movie.id);
    }
  }, [movie.id, isTV, prefetchMovie, prefetchTV]);

  const handleMouseLeave = useCallback(() => {
    if (isTV) {
      cancelTVPrefetch();
    } else {
      cancelMoviePrefetch();
    }
  }, [isTV, cancelMoviePrefetch, cancelTVPrefetch]);

  // Get genre names (first 2)
  const genreNames = movie.genre_ids
    ?.slice(0, 2)
    .map(id => genreMap[id] || GENRES[id])
    .filter(Boolean)
    .join(' / ') || '';

  return (
    <div
      className="group cursor-pointer"
      onClick={() => onClick?.(movie)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Poster with ratings overlay */}
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1F26] shadow-lg">
        {/* Placeholder - always visible until poster loads */}
        {movie.poster_path && !posterLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-800 animate-pulse">
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-4xl opacity-30">🎬</div>
            </div>
          </div>
        )}

        {movie.poster_path ? (
          <img
            src={getPosterUrl(movie.poster_path, 'large')}
            alt={movie.title}
            className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-[1.03] ${
              posterLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading={imagePriority === 'high' ? 'eager' : 'lazy'}
            fetchPriority={imagePriority === 'high' ? 'high' : undefined}
            onLoad={() => setPosterLoaded(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
            <div className="text-center p-4">
              <div className="text-5xl mb-2">🎬</div>
              <div className="text-slate-400 text-sm">{movie.title}</div>
            </div>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />

        {/* Ratings bar at bottom */}
        {hasRatings && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent pt-6 pb-1.5 px-1.5">
            <div className="flex justify-center gap-1">
              {/* User's Rating (shown first, most prominent) */}
              {displayUserRating && (
                <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-1.5 py-0.5 rounded flex items-center gap-0.5 shadow-lg">
                  <span className="text-[8px] font-bold text-white/80">YOU</span>
                  <span className="text-[10px] font-bold text-white">{displayUserRating}</span>
                </div>
              )}

              {/* Filmpl Rating */}
              {ourRating && (
                <div className="bg-purple-600/90 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <span className="text-[10px]">⭐</span>
                  <span className="text-[10px] font-bold text-white">{ourRating.toFixed(1)}</span>
                </div>
              )}

              {/* IMDB Rating */}
              {movie.imdb_rating && (
                <div className="bg-yellow-600/90 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <span className="text-[10px]">★</span>
                  <span className="text-[10px] font-bold text-white">{movie.imdb_rating.toFixed(1)}</span>
                </div>
              )}

              {/* Rotten Tomatoes */}
              {movie.rt_rating && (
                <div className="bg-red-600/90 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <span className="text-[10px]">🍅</span>
                  <span className="text-[10px] font-bold text-white">{movie.rt_rating}%</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Year badge */}
        <div className="absolute top-2 left-2 bg-[#0A0A0A]/70 text-white text-xs font-medium px-2 py-1 rounded">
          {year}
        </div>

        {/* TV indicator */}
        {isTV && (
          <div className="absolute top-2 right-2 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            TV
          </div>
        )}

        {/* Coming Soon pill for unreleased titles (below year badge) */}
        {comingOnLabel && (
          <div className="absolute top-9 left-2 bg-teal-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            Coming Soon
          </div>
        )}

        {/* Quick Action Button (top-right, shown on hover or when menu is open) */}
        {showQuickActions && user && (
          <div className={`absolute top-2 right-2 transition-opacity duration-200 ${
            isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}>
            <QuickActionButton
              movie={movie}
              onRateClick={() => setIsRatingModalOpen(true)}
              onOpenChange={setIsMenuOpen}
            />
          </div>
        )}
      </div>

      {/* Title and info below poster */}
      <div className="mt-2 px-1">
        <h3 className={`text-white font-medium text-sm line-clamp-2 transition-colors ${isTV ? 'group-hover:text-purple-400' : 'group-hover:text-cyan-400'}`}>
          {movie.title}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
          {genreNames && <span className="truncate">{genreNames}</span>}
          {genreNames && movie.runtime && <span>•</span>}
          {movie.runtime && <span>{isTV ? `${movie.runtime}m/ep` : formatRuntime(movie.runtime)}</span>}
          {isTV && movie.number_of_seasons && (
            <>
              <span>•</span>
              <span>{movie.number_of_seasons}S</span>
            </>
          )}
        </div>
      </div>

      {/* Rating Modal */}
      <RatingModal
        movie={movie}
        isOpen={isRatingModalOpen}
        onClose={() => setIsRatingModalOpen(false)}
      />
    </div>
  );
}
